import React, { useEffect, useRef, useState } from 'react'
import { MapPin } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { MAJOR_CITIES } from '../data/majorCities'

type Props = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

const MAX_SUGGESTIONS = 8

export const LocationAutocomplete: React.FC<Props> = ({ value, onChange, placeholder }) => {
  const { currentTheme: t } = useTheme()
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const query = value.trim().toLowerCase()
  const filtered = (query ? MAJOR_CITIES.filter((c) => c.toLowerCase().includes(query)) : MAJOR_CITIES).slice(0, MAX_SUGGESTIONS)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setActiveIndex(0)
  }, [value, open])

  const selectCity = (city: string) => {
    onChange(city)
    setOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || filtered.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      selectCity(filtered[activeIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition"
        style={{ backgroundColor: t.inputBackground, color: t.text, borderColor: t.border }}
      />
      {open && filtered.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1.5 z-20 max-h-56 overflow-y-auto custom-scrollbar rounded-xl py-1"
          style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}
        >
          {filtered.map((city, i) => (
            <button
              key={city}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectCity(city)}
              onMouseEnter={() => setActiveIndex(i)}
              className="flex items-center gap-2 w-full px-3.5 py-2 text-left text-sm transition-colors"
              style={{ backgroundColor: i === activeIndex ? t.hoverBackground : 'transparent', color: t.text }}
            >
              <MapPin size={13} style={{ color: t.mutedText }} className="shrink-0" />
              {city}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default React.memo(LocationAutocomplete)
